import { useEffect, useState } from 'react';
import { Button, DatePicker, Form, Input, InputNumber, Message, Select, Space, Switch, Typography } from '@arco-design/web-react';
import { getCdr } from '../api/cdr';
import { getCallByCdr, saveResultValues, type OutboundCallDetail, type OutboundResultField } from '../api/outbound';
import { outboundResultStatusMeta } from '../utils/labels';
import FormModal from './FormModal';
import PillTag from './PillTag';

/** 坐席工作台「外呼结果」填写面板（人工外呼/AI 转人工坐席承接）：
 * AI 预填草稿（result_values_draft）→ 坐席修改 → 「保存结果」确认（submitted）。
 * 挂断后随服务小结弹窗旁挂出；仅人工外呼（task_type=agent）且存在结果字段模板时展示。 */
export default function OutboundResultModal({
  visible,
  cdrEvent,
  onClose,
}: {
  visible: boolean;
  /** cdr_written 推送的本通 CDR（id=cdr_id；可能晚于弹窗到达） */
  cdrEvent?: { id: number; at: number };
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<OutboundCallDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!visible || !cdrEvent) return;
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    form.resetFields();
    // 先定位本通 CDR（uuid）→ 反查外呼号码 + 字段模板
    getCdr(cdrEvent.id)
      .then((cdr) => {
        if (!cdr?.uuid || cancelled) return null;
        return getCallByCdr(cdr.uuid);
      })
      .then((d) => {
        if (cancelled) return;
        if (!d) {
          setLoading(false);
          return; // 非外呼号码
        }
        setDetail(d);
        setLoading(false);
        const fields = d.fields || [];
        if (fields.length === 0) return;
        // 预填：AI 草稿优先，其次已确认值
        const source = d.result_values_draft || d.result_values || '';
        const prefill: Record<string, any> = {};
        if (source) {
          try {
            const parsed = JSON.parse(source);
            for (const f of fields) {
              if (parsed[f.field_key] !== undefined) prefill[f.field_key] = parsed[f.field_key];
            }
          } catch { /* 非法 JSON 忽略 */ }
        }
        form.setFieldsValue(prefill);
      })
      .catch(() => setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [visible, cdrEvent, form]);

  const onSave = async () => {
    if (!detail) return;
    const v = await form.validate();
    const values: Record<string, string> = {};
    for (const f of detail.fields || []) {
      const val = v[f.field_key];
      if (val === undefined || val === null || val === '') {
        if (f.required) {
          Message.warning(`必填字段「${f.label}」未填写`);
          return;
        }
        continue;
      }
      values[f.field_key] = String(val);
    }
    setSaving(true);
    try {
      await saveResultValues(detail.task_id, detail.call_id, values, 'submitted');
      Message.success('外呼结果已保存');
      onClose();
    } catch { /* 拦截器已提示（失败提示一元化） */ } finally {
      setSaving(false);
    }
  };

  const renderField = (f: OutboundResultField) => {
    const parseOpts = () => {
      try {
        const arr = JSON.parse(f.options || '[]');
        return Array.isArray(arr) ? arr.map((o: string) => ({ label: String(o), value: String(o) })) : [];
      } catch {
        return [];
      }
    };
    switch (f.field_type) {
      case 'select':
        return <Select options={parseOpts()} allowClear />;
      case 'multi_select':
        return <Select mode="multiple" options={parseOpts()} allowClear />;
      case 'number':
        return <InputNumber style={{ width: '100%' }} />;
      case 'date':
        return <DatePicker style={{ width: '100%' }} />;
      case 'boolean':
        return <Switch checkedText="是" uncheckedText="否" />;
      default:
        return <Input.TextArea rows={2} />;
    }
  };

  const fields = detail?.fields || [];
  const showModal = visible && !!detail && detail.task_type === 'agent' && fields.length > 0;

  return (
    <FormModal
      form={form}
      title={`外呼结果：${detail?.task_name || ''}`}
      visible={showModal}
      onCancel={onClose}
      onOk={onSave}
      okText="保存结果"
      confirmLoading={saving}
    >
      {loading ? (
        <Typography.Text type="secondary">加载外呼结果字段…</Typography.Text>
      ) : detail?.task_type === 'agent' && fields.length === 0 ? (
        <Typography.Text type="secondary">该人工外呼未配置结果字段模板（全局/任务场景均无），无需填写。</Typography.Text>
      ) : (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space wrap size={8}>
            {detail?.result_status && (
              <PillTag color={outboundResultStatusMeta(detail.result_status)[1]}>
                当前状态：
                {outboundResultStatusMeta(detail.result_status)[0]}
              </PillTag>
            )}
            {detail?.result_values_draft && (
              <Typography.Text type="secondary">已有 AI 预填草稿，可修改后确认</Typography.Text>
            )}
          </Space>
          <Form form={form} layout="vertical">
            {fields.map((f) => (
              <Form.Item
                key={f.field_key}
                field={f.field_key}
                label={`${f.label}${f.required ? ' *' : ''}`}
                extra={f.remark}
              >
                {renderField(f)}
              </Form.Item>
            ))}
          </Form>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            结果保存（submitted）后计入统计；未保存仅 AI 草稿（待确认），不入统计。
          </Typography.Text>
        </Space>
      )}
    </FormModal>
  );
}
